import { Modal } from "react-bootstrap";
import { t } from "../../i18n";

/**
 * The last save stored the target but not its SEO. It outlives the dismissed modal so the host's
 * success message stays hidden until the next save.
 */
export interface SeoFailureType {
  reason: string;
  dismissed: boolean;
}

export interface SeoSaveFailedModalProps {
  /** Says what was saved and why the SEO was not. */
  message: string;
  testId: string;
  /** Only dismisses the modal; the editor stays open with the drafts for another save. */
  onContinue: () => void;
}

/** Tells the operator their target saved while its SEO did not. */
export function SeoSaveFailedModal({ message, testId, onContinue }: SeoSaveFailedModalProps): JSX.Element {
  return (
    <Modal
      show={true}
      onHide={onContinue}
    >
      <Modal.Header>{t("admin.seo.save-failed-title", undefined, "SEO not saved")}</Modal.Header>
      <Modal.Body>
        <div
          className="alert alert-warning"
          data-testid={testId}
        >
          {message}
        </div>
        <button
          type="button"
          className="btn btn-primary"
          data-testid={`${testId}-continue`}
          onClick={onContinue}
        >
          {t("admin.seo.continue", undefined, "Continue")}
        </button>
      </Modal.Body>
    </Modal>
  );
}

/** The host's success message only belongs to a save that stored the SEO too. */
export function isSeoSaved(seoFailure: SeoFailureType | undefined): boolean {
  return seoFailure === undefined;
}

export interface SeoFailureModalProps {
  seoFailure?: SeoFailureType;
  /** Translation key of the message; it receives the failure as `reason`. */
  messageKey: string;
  messageFallback: string;
  testId: string;
  onDismiss: (dismissed: SeoFailureType) => void;
}

/** Shows a host's SEO failure until the operator dismisses it. */
export function SeoFailureModal({
  seoFailure,
  messageKey,
  messageFallback,
  testId,
  onDismiss,
}: SeoFailureModalProps): JSX.Element | null {
  if (seoFailure === undefined || seoFailure.dismissed) {
    return null;
  }
  return (
    <SeoSaveFailedModal
      message={t(messageKey, { reason: seoFailure.reason, interpolation: { escapeValue: false } }, messageFallback)}
      testId={testId}
      onContinue={() => onDismiss({ ...seoFailure, dismissed: true })}
    />
  );
}
